import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { localizePath } from "@/utils/languageRoutes";

interface DemoCtaProps {
  language: string;
}

const CTA_PATH = "M 40 70 Q 300 -30 560 70";

const DemoCta = ({ language }: DemoCtaProps) => {
  const isKo = language === "KO";

  return (
    <section className="relative overflow-hidden bg-white py-28 md:py-36">
      <div
        aria-hidden
        className="absolute left-1/2 top-0 h-px w-[min(90%,72rem)] -translate-x-1/2 bg-gradient-to-r from-transparent via-slate-200 to-transparent"
      />
      <div aria-hidden className="absolute inset-0 [background:radial-gradient(640px_circle_at_50%_0%,rgba(20,52,164,0.06),transparent)]" />

      <div className="container relative mx-auto max-w-3xl px-4 text-center">
        {/* mini corridor arcing over the headline */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="relative mx-auto mb-2 h-24 w-full max-w-xl"
        >
          <svg viewBox="0 0 600 100" fill="none" className="h-full w-full" preserveAspectRatio="xMidYMid meet">
            <path
              d={CTA_PATH}
              stroke="#CBD5E1"
              strokeWidth="2"
              strokeDasharray="6 10"
              strokeLinecap="round"
              style={{ animation: "demo-dash 2.4s linear infinite" }}
            />
            <g>
              <circle r="8" fill="#FF9F1C" opacity="0.2">
                <animateMotion dur="4s" repeatCount="indefinite" path={CTA_PATH} />
              </circle>
              <circle r="4" fill="#FF9F1C">
                <animateMotion dur="4s" repeatCount="indefinite" path={CTA_PATH} />
              </circle>
            </g>
            <circle cx="40" cy="70" r="5" fill="#1434A4" />
            <circle cx="560" cy="70" r="5" fill="#FF9F1C" />
          </svg>
          <span className="absolute bottom-0 left-[3%] text-xl" role="img" aria-label="South Korea flag">🇰🇷</span>
          <span className="absolute bottom-0 right-[3%] text-xl" role="img" aria-label="India flag">🇮🇳</span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 28, filter: "blur(6px)" }}
          whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="text-4xl font-bold tracking-tight text-slate-900 md:text-6xl"
        >
          {isKo ? "인도 진출," : "Ready to build"}
          <br />
          <span className="relative inline-block text-primary">
            {isKo ? "지금 시작하세요" : "in India?"}
            <motion.svg
              viewBox="0 0 200 12"
              fill="none"
              preserveAspectRatio="none"
              className="absolute -bottom-2 left-0 h-[0.16em] w-full"
              aria-hidden
            >
              <motion.path
                d="M3 9 C 50 2, 150 2, 197 7"
                stroke="#FF9F1C"
                strokeWidth="6"
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.4, ease: "easeOut" }}
              />
            </motion.svg>
          </span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-slate-600"
        >
          {isKo
            ? "첫 상담부터 법인 설립, 운영까지 — 두루가 전 과정을 함께합니다."
            : "From the first consultation to incorporation and day-to-day operations — Duru walks the whole road with you."}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row"
        >
          <Link to={localizePath("/contact", language)}>
            <Button
              size="lg"
              className="group relative overflow-hidden rounded-xl bg-primary px-8 py-6 text-base font-semibold text-white shadow-lg shadow-primary/25 transition-all hover:bg-primary-dark hover:shadow-xl"
            >
              <span
                aria-hidden
                className="absolute inset-0 -translate-x-[130%] bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-[130%]"
              />
              {isKo ? "전문가와 상담하기" : "Talk to an Expert"}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
          <a
            href="mailto:info@duruper.com"
            className="text-sm font-semibold text-slate-600 underline-offset-4 transition-colors hover:text-primary hover:underline"
          >
            info@duruper.com
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default DemoCta;
